/**
 * Frontend-Hilfsfunktionen für die Nachvollziehbarkeit beim Umbuchen (Transfer)
 * einer bezahlten Buchung in einen anderen Kurs.
 *
 * Quelle der Wahrheit ist das strukturierte Objekt `transferContext`, das das
 * Backend im JSONB-Feld `BookingHistory.newValues` (neue Buchung, direction
 * "in") bzw. `oldValues` (alte Buchung, direction "out") ablegt — KEIN eigenes
 * GraphQL-Feld (kein Codegen nötig). Sekundär kann der `noticeStaff`-Text der
 * Buchung als Fallback dienen (siehe {@link detectTransferPaidFromNoticeStaff}).
 */

export type TransferContext = {
    direction: "in" | "out";
    sourceBookingNumber?: string;
    sourceCourseNumber?: string;
    targetBookingNumber?: string;
    targetCourseNumber?: string;
    transferredAmount: number;
    currency: string;
};

/**
 * Liest einen `transferContext` aus einem `oldValues`/`newValues`-JSONB-Objekt
 * der History aus. Defensiv getypt, da das JSONB-Schema nicht streng validiert
 * ist und ältere Einträge das Feld nicht enthalten.
 */
export const extractTransferContext = (values: unknown): TransferContext | undefined => {
    if (values === null || values === undefined || typeof values !== "object") {
        return undefined;
    }

    const candidate = (values as {transferContext?: unknown}).transferContext;

    if (candidate === null || candidate === undefined || typeof candidate !== "object") {
        return undefined;
    }

    const tc = candidate as Partial<TransferContext>;

    if (typeof tc.transferredAmount !== "number" || tc.transferredAmount <= 0) {
        return undefined;
    }

    const direction = tc.direction === "out" ? "out" : "in";

    return {
        direction,
        sourceBookingNumber: typeof tc.sourceBookingNumber === "string" ? tc.sourceBookingNumber : undefined,
        sourceCourseNumber: typeof tc.sourceCourseNumber === "string" ? tc.sourceCourseNumber : undefined,
        targetBookingNumber: typeof tc.targetBookingNumber === "string" ? tc.targetBookingNumber : undefined,
        targetCourseNumber: typeof tc.targetCourseNumber === "string" ? tc.targetCourseNumber : undefined,
        transferredAmount: tc.transferredAmount,
        currency: typeof tc.currency === "string" ? tc.currency : "EUR",
    };
};

/**
 * Erkennt anhand der `noticeStaff`-Notiz der NEUEN Buchung, ob ihr Preis durch
 * einen Übertrag aus einer Umbuchung gedeckt wurde. Das Backend schreibt beim
 * Übertrag den Marker "Übertrag von {Betrag} {Währung} durch Umbuchung."
 * (booking.functions.transferMultipleBookings.ts).
 *
 * Wird als robuster Fallback im Zahlungs-UI genutzt, wo die strukturierte
 * History nicht immer geladen ist.
 */
export const detectTransferPaidFromNoticeStaff = (
    noticeStaff: string | null | undefined,
): {isTransferPaid: boolean; amount?: string; currency?: string} => {
    if (!noticeStaff) {
        return {isTransferPaid: false};
    }

    // Beispiel-Zeile: "- 02.06.26 - Übertrag von 100,00 EUR durch Umbuchung."
    // Betrag kann Komma (EUR) oder Punkt (CHF/...) als Dezimaltrennzeichen nutzen.
    const match = noticeStaff.match(/Übertrag von ([\d.,]+)\s+([A-Z]{3}) durch Umbuchung/);

    if (!match) {
        return {isTransferPaid: false};
    }

    return {isTransferPaid: true, amount: match[1], currency: match[2]};
};
